import { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const SITE_FIELDS = [
    ["business_name", "Business Name", "text"],
    ["tagline", "Tagline", "text"],
    ["about_short", "About (short)", "text"],
    ["about_long", "About (long)", "textarea"],
    ["address", "Address", "text"],
    ["email", "Public Email", "text"],
    ["phone", "Phone", "text"],
    ["whatsapp", "WhatsApp", "text"],
    ["instagram_url", "Instagram URL", "text"],
    ["facebook_url", "Facebook URL", "text"],
    ["twitter_url", "Twitter URL", "text"],
    ["youtube_url", "YouTube URL", "text"],
];

const SMTP_FIELDS = [
    ["smtp_host", "SMTP Host", "text", "mail.cakecentral.com.au"],
    ["smtp_port", "SMTP Port", "number", "587"],
    ["smtp_username", "SMTP Username", "text", "noreply@cakecentral.com.au"],
    ["smtp_password", "SMTP Password", "password", ""],
    ["smtp_from", "From Address", "text", "noreply@cakecentral.com.au"],
    ["notify_email", "Notify Inquiries To", "text", "info@cakecentral.com.au"],
];

export default function AdminSettings() {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [newHeroUrl, setNewHeroUrl] = useState("");

    useEffect(() => {
        api.get("/admin/settings").then((r) => setForm(r.data));
    }, []);

    const save = async (e) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);
        try {
            await api.put("/settings", form);
            toast.success("Settings updated");
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const testEmail = async () => {
        if (testing) return;
        setTesting(true);
        try {
            const r = await api.post("/admin/test-email", { to: form.notify_email || form.email });
            toast.success(`Test email sent to ${r.data.sent_to}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Test failed");
        } finally {
            setTesting(false);
        }
    };

    const renderField = ([k, label, type, placeholder = ""]) => (
        <div key={k}>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">{label}</label>
            {type === "textarea" ? (
                <textarea
                    rows={4}
                    value={form[k] || ""}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                    data-testid={`settings-${k}`}
                />
            ) : (
                <input
                    type={type}
                    placeholder={placeholder}
                    value={form[k] ?? ""}
                    onChange={(e) => setForm({ ...form, [k]: type === "number" ? parseInt(e.target.value) || 0 : e.target.value })}
                    className="w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                    data-testid={`settings-${k}`}
                />
            )}
        </div>
    );

    return (
        <div data-testid="admin-settings" className="space-y-8 max-w-3xl">
            <div>
                <h2 className="font-display text-3xl mb-1">Site Settings</h2>
                <p className="text-sm text-ink-soft">Public-facing info, social links, and email notifications.</p>
            </div>

            <form onSubmit={save} className="space-y-8">
                <section className="bg-white border border-[#EAE3DE] p-6 md:p-8 space-y-4">
                    <h3 className="font-display text-xl mb-2">Public</h3>
                    {SITE_FIELDS.map(renderField)}
                </section>

                {/* ── Hero Images ─────────────────────────────────────────────────── */}
                <section className="bg-white border border-[#EAE3DE] p-6 md:p-8 space-y-4">
                    <div>
                        <h3 className="font-display text-xl mb-1">Hero Images</h3>
                        <p className="text-xs text-ink-soft">
                            Portrait images shown in the hero slider. Recommend 4–8 images.
                            Even-numbered images (1st, 3rd…) appear on the <strong>left</strong>; odd-numbered on the <strong>right</strong>.
                        </p>
                    </div>

                    {/* Thumbnail grid */}
                    {(form.hero_images || []).length > 0 && (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                            {(form.hero_images || []).map((url, i) => (
                                <div key={i} className="relative group aspect-[2/3] overflow-hidden bg-[#F9F5F2]">
                                    <img
                                        src={url}
                                        alt={`Hero ${i + 1}`}
                                        className="w-full h-full object-cover object-center"
                                        loading="lazy"
                                    />
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setForm({
                                                ...form,
                                                hero_images: form.hero_images.filter((_, j) => j !== i),
                                            })
                                        }
                                        className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 rounded-sm"
                                        aria-label={`Remove image ${i + 1}`}
                                        data-testid={`hero-img-remove-${i}`}
                                    >
                                        ×
                                    </button>
                                    <span className="absolute bottom-1 left-1 text-[9px] bg-black/50 text-white px-1 rounded leading-tight">
                                        {i % 2 === 0 ? "L" : "R"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add new image URL */}
                    <div className="flex gap-2">
                        <input
                            type="url"
                            placeholder="https://images.unsplash.com/photo-…"
                            value={newHeroUrl}
                            onChange={(e) => setNewHeroUrl(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    if (!newHeroUrl.trim()) return;
                                    setForm({ ...form, hero_images: [...(form.hero_images || []), newHeroUrl.trim()] });
                                    setNewHeroUrl("");
                                }
                            }}
                            className="flex-1 px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush text-sm"
                            data-testid="hero-image-url-input"
                        />
                        <button
                            type="button"
                            onClick={() => {
                                if (!newHeroUrl.trim()) return;
                                setForm({ ...form, hero_images: [...(form.hero_images || []), newHeroUrl.trim()] });
                                setNewHeroUrl("");
                            }}
                            className="btn-pink shrink-0"
                            data-testid="hero-image-add-btn"
                        >
                            Add
                        </button>
                    </div>
                </section>

                {/* ── SMTP ─────────────────────────────────────────────────────────── */}
                <section className="bg-white border border-[#EAE3DE] p-6 md:p-8 space-y-4">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h3 className="font-display text-xl">Email Notifications (SMTP)</h3>
                            <p className="text-xs text-ink-soft mt-1">
                                Use your own SMTP server. Common ports: 587 (STARTTLS), 465 (SSL).
                                Leave blank to disable email notifications.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={testEmail}
                            disabled={testing || !form.smtp_host}
                            className="btn-pink disabled:opacity-50"
                            data-testid="test-email-btn"
                        >
                            <Mail size={14} /> {testing ? "Sending…" : "Send Test"}
                        </button>
                    </div>
                    {SMTP_FIELDS.map(renderField)}
                    <label className="flex items-center gap-2 pt-2">
                        <input
                            type="checkbox"
                            checked={form.smtp_use_tls ?? true}
                            onChange={(e) => setForm({ ...form, smtp_use_tls: e.target.checked })}
                            data-testid="settings-smtp_use_tls"
                        />
                        <span className="text-sm">Use STARTTLS (recommended for port 587)</span>
                    </label>
                </section>

                <button type="submit" disabled={saving} className="btn-pink disabled:opacity-60" data-testid="save-settings">
                    {saving ? "Saving…" : "Save Settings"}
                </button>
            </form>
        </div>
    );
}
